import {
  useEffect,
  useRef,
  useState
} from 'react'
import { useSelector } from 'react-redux'
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Stack,
  Text
} from '@chakra-ui/react'
import { LoadingSpinier } from 'common/Loading'
import {
  usersSelectors,
  markersSelectors,
  chatCredentialsSelectors,
  setNotificationSectionPage
} from 'store'
import {
  NOTIFICATIONS_TITLE,
  NotificationSections,
  PENDING
} from 'utils/constants'
import {
  createValidFormatData,
  isAuth
} from 'utils/helpers'
import { COLORS } from 'utils/styles'

import { ContentItem } from './ContentItem'
import { FriendRequest } from './ConnectionRequests/FriendRequest'
import { BlockedRequest } from './ConnectionRequests/BlockedRequest'
import { BlogPreference } from './CommunityBlogs/BlogPreference'
import { CommunityBlogs } from './CommunityBlogs'
import { RequestDetails } from './ConnectionRequests/RequestDetails'
import { Engagements } from './Engagements'
import { EngagementItemList } from './Engagements/EngagementItemList'
import { SavedEvents } from './SavedEvents'

export const Notifications = ({
  pageName,
  setShowNotifications,
  showNotifications
}) => {
  const {
    activeProfile,
    adminNotifRequest,
    adminNotifications,
    blogPreference,
    getAdminNotificationsStatus,
    getBlockedRequestStatus,
    getBlogPreferenceStatus,
    getEngagementItemsStatus,
    getFriendsNotificationsStatus,
    getPostsStatus,
    getProfileItemStatus,
    getSavedEventsStatus,
    isNotifViewing,
    notificationSectionPage,
    openPopupBlogs,
    postMakeNotificationSeenAllStatus,
    postMakeNotificationSeenStatus,
    savedEvents,
    userProfile
  } = useSelector(usersSelectors)

  const {
    getEventDataStatus,
    singleMarkerUpdateStatus
  } = useSelector(markersSelectors)

  const { meXmppProblem } = useSelector(chatCredentialsSelectors)

  const cardRef = useRef()

  const [selectedKey, setSelectedKey] = useState(null)
  const [selectedEngType, setSelectedEngType] = useState('')

  window.addEventListener('beforeunload', () => {
    if (localStorage.getItem('isNotifOpen') === 'true') {
      setShowNotifications(false)
    }
  })

  useEffect(() => {
    if (
      getAdminNotificationsStatus === 'SUCCEEDED'
      && getFriendsNotificationsStatus === 'SUCCEEDED'
      && getBlockedRequestStatus === 'PENDING'
    ) {
      setShowNotifications(true)
    }
  }, [getAdminNotificationsStatus, getFriendsNotificationsStatus])

  useEffect(() => {
    if (getFriendsNotificationsStatus !== 'PENDING' && getBlockedRequestStatus !== 'PENDING') {
      localStorage.setItem('notif-request-loaded', true)
    } else {
      localStorage.setItem('notif-request-loaded', false)
    }
  }, [getFriendsNotificationsStatus, getBlockedRequestStatus])

  useEffect(() => {
    setSelectedKey(null)
    setSelectedEngType('')
  }, [notificationSectionPage])

  const notificationsTitle = (title) => {
    switch (title) {
    case NOTIFICATIONS_TITLE.Engagements:
      return 'Engagements'
    case NOTIFICATIONS_TITLE.Connector:
      return 'Connector'
    case NOTIFICATIONS_TITLE.Proximity:
      return 'Proximity'
    case NOTIFICATIONS_TITLE.NewUsers:
      return 'New Users'
    case NOTIFICATIONS_TITLE.Concerts:
      return 'Concerts and Events'
    case NOTIFICATIONS_TITLE.CommunityBlogs:
      return 'Community Blogs'
    case NOTIFICATIONS_TITLE.Advertisers:
      return 'Advertisers'
    default:
      return ''
    }
  }

  const getCurrentPage = () => {
    switch (pageName) {
    case NotificationSections.BLOG_PREFERENCE:
      return (
        <BlogPreference
          blogPreference={blogPreference}
          getBlogPreferenceStatus={getBlogPreferenceStatus}
        />
      )
    case NotificationSections.ENGAGEMENT_ITEM_LIST:
      return (<EngagementItemList activeProfile={activeProfile} />)
    case NotificationSections.SAVED_EVENTS_LIST:
      return (
        <SavedEvents
          getSavedEventsStatus={getSavedEventsStatus}
          openPopupBlogs={openPopupBlogs}
          savedEvents={savedEvents}
        />
      )
    case NotificationSections.CONNECTION_REQUEST:
      return <RequestDetails />
    default:
      return (
        <Stack
          margin="0px"
          overflowY="auto"
          overflowX="hidden"
          height="100%"
          gap="0px !important"
          className="notif-container"
          sx={{
            '::-webkit-scrollbar': {
              display: 'none'
            },
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none'
          }}
        >
          {createValidFormatData(adminNotifications).map((item) => {
            const title = notificationsTitle(item.id)

            if (title === '') {
              return ''
            }

            if (title === 'Community Blogs') {
              return (
                <CommunityBlogs
                  getBlogPreferenceStatus={getBlogPreferenceStatus}
                  getEngagementItemsStatus={getEngagementItemsStatus}
                  getEventDataStatus={getEventDataStatus}
                  getPostsStatus={getPostsStatus}
                  getProfileItemStatus={getProfileItemStatus}
                  getSavedEventsStatus={getSavedEventsStatus}
                  NotificationSections={NotificationSections}
                  postMakeNotificationSeenAllStatus={postMakeNotificationSeenAllStatus}
                  postMakeNotificationSeenStatus={postMakeNotificationSeenStatus}
                  preferredBlogs={item?.value}
                  rowTitle={title}
                  singleMarkerUpdateStatus={singleMarkerUpdateStatus}
                  setNotificationSectionPage={setNotificationSectionPage}
                />
              )
            }

            if (title === 'Engagements') {
              return (
                <>
                  <Engagements
                    engagementTypes={item?.value}
                    getBlogPreferenceStatus={getBlogPreferenceStatus}
                    getEngagementItemsStatus={getEngagementItemsStatus}
                    getEventDataStatus={getEventDataStatus}
                    getPostsStatus={getPostsStatus}
                    getProfileItemStatus={getProfileItemStatus}
                    getSavedEventsStatus={getSavedEventsStatus}
                    postMakeNotificationSeenAllStatus={postMakeNotificationSeenAllStatus}
                    postMakeNotificationSeenStatus={postMakeNotificationSeenStatus}
                    rowTitle={title}
                    singleMarkerUpdateStatus={singleMarkerUpdateStatus}
                    selectedEngType={selectedEngType}
                    setSelectedEngType={setSelectedEngType}
                  />
                  <FriendRequest />
                </>
              )
            }

            const isViewedNotification = item?.value?.filter(
              (n) => n?.is_viewed === 0
            ).length

            return (
              <Card
                ref={cardRef}
                boxShadow="none"
                marginTop="0px !important"
                background="#c4c3c7"
              >
                <CardHeader
                  display="flex"
                  alignItems="center"
                  padding="0 10px"
                  background="#feeb88"
                >
                  <Box
                    textAlign="left"
                    paddingRight="10px"
                  >
                    {title}
                  </Box>

                  {isViewedNotification > 0 && (
                    <Stack
                      direction="row"
                      alignItems="center"
                    >
                      <Box
                        textAlign="center"
                        background={COLORS.gold}
                        width="17px"
                        height="17px"
                        borderRadius="full"
                        position="relative"
                      >
                        <Box
                          fontSize="12px"
                          fontWeight={500}
                          transform="translate(-50%, -50%)"
                          top="50%"
                          left="50%"
                          position="absolute"
                        >
                          {isViewedNotification > 99 ? '99+' : isViewedNotification}
                        </Box>
                      </Box>
                    </Stack>
                  )}
                </CardHeader>

                <CardBody
                  padding="5px"
                  minHeight="130px"
                  maxHeight="130px"
                  verflow="hidden"
                >
                  <Box
                    marginLeft="5px"
                    marginRight="5px"
                    sx={{
                      overflowX: 'auto',
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '10px'
                    }}
                    css={{
                      '&::-webkit-scrollbar': {
                        width: '4px',
                        height: '10px',
                        display: 'block'
                      },
                      '&::-webkit-scrollbar-track': {
                        width: '6px'
                      },
                      '&::-webkit-scrollbar-thumb': {
                        borderRadius: '24px',
                        background: 'rgba(255, 215, 0, 1)'
                      }
                    }}
                  >
                    <ContentItem
                      items={item?.value}
                      title={item?.id}
                      selectedKey={selectedKey}
                      setSelectedKey={setSelectedKey}
                    />
                  </Box>
                </CardBody>
              </Card>
            )
          })}
          <BlockedRequest />
        </Stack>
      )
    }
  }

  const whatToShow = () => {
    if (userProfile && !userProfile.find((profile) => profile.is_active)) {
      return (
        <Stack
          margin="16px 0"
          gap="16px 0"
        >
          <Text
            textAlign="center"
            backgroundColor="rgb(229, 229, 229)"
            borderRadius="3px"
            border="solid 1px black"
            padding="10px"
            margin="0 2% !important"
            fontSize="13px"
          >
            Create a profile to get notifications
          </Text>
        </Stack>
      )
    }

    if (
      adminNotifRequest === null
      || (
        getAdminNotificationsStatus === PENDING
        && !isNotifViewing
      )
      || (
        !isAuth()
        && (
          !showNotifications
          || meXmppProblem
        )
      )
    ) {
      return <LoadingSpinier />
    }

    return getCurrentPage()
  }

  return whatToShow()
}
